import { useEffect, useState, useRef, useMemo, useCallback } from "react"
import Mapbox, { Source, Layer, Marker } from "react-map-gl/mapbox"
import type { MapRef, MapMouseEvent } from "react-map-gl/mapbox"
import type { CircleLayerSpecification, SymbolLayerSpecification } from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import { LocalOverlay, LatLng } from "./localOverlays"
import { ENVS, isStaffOnEnv } from "./session"
import iconCattle from "./assets/iconCattle.svg"
import iconSmall from "./assets/iconSmall.svg"
import userLocationIcon from "./assets/userLocation.svg"
import CustomerInfoModal from "./CustomerInfoModal"
import CollarInfoModal from "./CollarInfoModal"
import ForceUserLocationModal from "./ForceUserLocationModal"
import MapInfo from "./MapInfo"

type Collar = {
  serialNo: number
  position: {
    type: "Point"
    coordinates: number[]
  }
  type: "small" | "cattle"
}

export type Owner = {
  name: string
  email: string
}

type Pasture = {
  id: number
  geometry: {
    type: "Polygon"
    coordinates: number[][][]
  } | null
  owner?: Owner
}

// "icon": custom SVG markers. "circle": colored dots (cattle = purple, sg = red),
// far lighter to render than symbols — the escape hatch for the whole-map view.
export type CollarStyle = "icon" | "circle"

// "globe" 3D sphere vs "mercator" flat map.
export type MapProjection = "globe" | "mercator"

// Basemap selector. "terrain" keeps the satellite imagery but adds a 3D DEM
// relief; the others are plain 2D styles.
export type Basemap = "satellite" | "streets" | "outdoors" | "terrain"

export const BASEMAPS: Basemap[] = ["satellite", "streets", "outdoors", "terrain"]

const BASEMAP_STYLE: Record<Basemap, string> = {
  satellite: "mapbox://styles/mapbox/satellite-streets-v12",
  streets: "mapbox://styles/mapbox/streets-v12",
  outdoors: "mapbox://styles/mapbox/outdoors-v12",
  terrain: "mapbox://styles/mapbox/satellite-streets-v12", // satellite + 3D relief
}

// Mapbox-hosted digital elevation model, used for 3D terrain.
const DEM_SOURCE = "mapbox-dem"
const DEM_URL = "mapbox://mapbox.mapbox-terrain-dem-v1"
const TERRAIN_EXAGGERATION = 1.5
const TERRAIN_PITCH = 60

const INITIAL_CENTER = { lat: 60, lng: 8 }
const INITIAL_ZOOM = 2
const MIN_ZOOM = 2
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || ""

const PASTURE_COLOR = "#64A70B"
const CATTLE_COLOR = "#800080" // purple
const SG_COLOR = "#FF0000" // red (small grazer: sheep/goat)

// Layers a click/hover can hit. A hidden layer (visibility:none) returns no
// features, so listing both collar layers here is safe regardless of style.
const COLLAR_ICON_LAYER = "collar-icons"
const COLLAR_CIRCLE_LAYER = "collar-circles"
const PASTURE_FILL_LAYER = "pasture-fill"
// Overlay layer ids are per-overlay: `overlay-{fill,line,point}-<overlay id>`.
const isOverlayLayer = (id: string | undefined) => !!id && id.startsWith("overlay-")

const collarIconLayer = (visible: boolean, opacity: number): SymbolLayerSpecification => ({
  id: COLLAR_ICON_LAYER,
  type: "symbol",
  source: "collars",
  layout: {
    visibility: visible ? "visible" : "none",
    "icon-image": ["match", ["get", "type"], "small", "collar-small", "collar-cattle"],
    "icon-size": 0.8,
    // Draw every animal — without these, Mapbox hides overlapping icons and
    // we'd silently lose points at scale.
    "icon-allow-overlap": true,
    "icon-ignore-placement": true,
  },
  paint: { "icon-opacity": opacity },
})

const collarCircleLayer = (visible: boolean, opacity: number): CircleLayerSpecification => ({
  id: COLLAR_CIRCLE_LAYER,
  type: "circle",
  source: "collars",
  layout: { visibility: visible ? "visible" : "none" },
  paint: {
    "circle-radius": 5,
    "circle-color": ["match", ["get", "type"], "cattle", CATTLE_COLOR, SG_COLOR],
    "circle-stroke-width": 1,
    "circle-stroke-color": "#fff",
    "circle-opacity": opacity,
    "circle-stroke-opacity": opacity,
  },
})

type FC = GeoJSON.FeatureCollection
const toLngLat = (p: LatLng): [number, number] => [p.lng, p.lat]

function Map({
  height,
  overlays = [],
  collarStyle = "icon",
  projection = "globe",
  basemap = "satellite",
  collarOpacity = 1,
}: {
  height: string
  overlays?: LocalOverlay[]
  collarStyle?: CollarStyle
  projection?: MapProjection
  basemap?: Basemap
  collarOpacity?: number
}) {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number }>()
  const [collars, setCollars] = useState<Collar[]>([])
  const [pastures, setPastures] = useState<Pasture[]>([])
  const [showUserInfoModal, setShowUserInfoModal] = useState(false)
  const [showCollarInfoModal, setShowCollarInfoModal] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState<Owner>()
  const [selectedCollar, setSelectedCollar] = useState<string>()
  const [showForceUserLocationModal, setShowForceUserLocationModal] = useState(false)
  const [isStaff, setIsStaff] = useState(false)
  const [hoveredSerial, setHoveredSerial] = useState<number>()
  const [hoveredOverlay, setHoveredOverlay] = useState<{ name: string; description: string }>()
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>()
  const [loading, setLoading] = useState(false)
  const mapRef = useRef<MapRef | null>(null)
  // Rasterized collar icons, cached so they can be re-added after a basemap
  // switch (changing the style wipes images added via addImage).
  const collarImages = useRef<Record<string, ImageData>>({})

  // Show every collar regardless of uploaded KML/KMZ overlays. Points and the
  // user-uploaded map layer are displayed independently — no filtering.
  const visibleCollars = collars

  // One GeoJSON source feeds both the symbol and circle collar layers. Carrying
  // serialNo/type as feature properties lets layer styling and click/hover read
  // them directly — this is what scales to 100k points (GPU layer, not DOM).
  const collarsFC = useMemo<FC>(
    () => ({
      type: "FeatureCollection",
      features: visibleCollars
        .filter((c) => c.position)
        .map((c) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: c.position.coordinates },
          properties: { serialNo: c.serialNo, type: c.type },
        })),
    }),
    [visibleCollars],
  )

  // Pasture geometry is already GeoJSON Polygon — no conversion needed. Owners
  // are kept in a side lookup (nested props don't survive a GeoJSON source).
  const pasturesFC = useMemo<FC>(
    () => ({
      type: "FeatureCollection",
      features: pastures
        .filter((p) => p.geometry)
        .map((p) => ({
          type: "Feature",
          geometry: p.geometry as GeoJSON.Polygon,
          properties: { id: p.id },
        })),
    }),
    [pastures],
  )

  const ownersById = useMemo(() => {
    const m: Record<number, Owner> = {}
    for (const p of pastures) if (p.owner) m[p.id] = p.owner
    return m
  }, [pastures])

  // One renderable entry per visible overlay. Each gets its own Source + Layers
  // so its opacity can be a plain constant (per-overlay, user-adjustable).
  const overlayLayers = useMemo(
    () =>
      overlays
        .filter((o) => o.visible)
        .map((o) => {
          const features: GeoJSON.Feature[] = []
          for (const f of o.features) {
            const props = { name: f.name ?? "", description: f.description ?? "" }
            if (f.kind === "point") {
              features.push({
                type: "Feature",
                geometry: { type: "Point", coordinates: toLngLat(f.position) },
                properties: props,
              })
            } else if (f.kind === "line") {
              features.push({
                type: "Feature",
                geometry: { type: "LineString", coordinates: f.path.map(toLngLat) },
                properties: props,
              })
            } else {
              features.push({
                type: "Feature",
                geometry: { type: "Polygon", coordinates: f.rings.map((r) => r.map(toLngLat)) },
                properties: props,
              })
            }
          }
          return {
            id: o.id,
            color: o.color,
            opacity: o.opacity,
            fc: { type: "FeatureCollection", features } as FC,
          }
        }),
    [overlays],
  )

  // Every overlay layer id, so hover/click hit-testing can target them.
  const overlayLayerIds = useMemo(
    () => overlayLayers.flatMap((o) => [`overlay-fill-${o.id}`, `overlay-line-${o.id}`, `overlay-point-${o.id}`]),
    [overlayLayers],
  )

  const interactiveLayerIds = useMemo(
    () => [COLLAR_ICON_LAYER, COLLAR_CIRCLE_LAYER, PASTURE_FILL_LAYER, ...overlayLayerIds],
    [overlayLayerIds],
  )

  useEffect(() => {
    initialize()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userLocation && !isStaff) {
      fetchData(userLocation)
    }
  }, [userLocation, isStaff]) // eslint-disable-line react-hooks/exhaustive-deps

  const STAFF_FETCH_CENTER = { lat: 60, lng: 8 } // ignored by backend staff branch, but center is required

  async function initialize() {
    const sessions = await Promise.all(ENVS.map(isStaffOnEnv))
    const staff = sessions.some(Boolean)
    if (staff) {
      setIsStaff(true)
      fetchData(STAFF_FETCH_CENTER)
      return
    }
    getUserLocation()
  }

  function getUserLocation() {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = { lat: position.coords.latitude, lng: position.coords.longitude }
        setUserLocation(loc)
        mapRef.current?.flyTo({ center: [loc.lng, loc.lat], zoom: 16 })
        setShowForceUserLocationModal(false)
      },
      (error) => {
        console.log(error)
        setShowForceUserLocationModal(true)
      },
    )
  }

  async function fetchData(center: { lat: number; lng: number }) {
    const empty = { collars: [], pastures: [] }

    const fetchOne = (base: string) =>
      fetch(`${base}/api/open/data/?center=${center.lng},${center.lat}`, {
        method: "GET",
        credentials: "include",
      })
        .then((response) => {
          if (response.status !== 200) {
            throw Error("Request error")
          }
          return response.json()
        })
        .catch((error) => {
          console.log(error)
          return empty
        })

    const showSpinnerTimer = setTimeout(() => setLoading(true), 400)
    try {
      const results: { collars: Collar[]; pastures: Pasture[] }[] = await Promise.all(
        ENVS.map(fetchOne),
      )
      setCollars(results.flatMap((r) => r.collars))
      setPastures(results.flatMap((r) => r.pastures))
    } finally {
      clearTimeout(showSpinnerTimer)
      setLoading(false)
    }
  }

  function refresh() {
    if (isStaff) {
      fetchData(STAFF_FETCH_CENTER)
    } else {
      getUserLocation()
    }
  }

  // Pan/zoom to newly loaded overlays so staff immediately see what they added.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !overlays.length) return
    let minLng = Infinity
    let minLat = Infinity
    let maxLng = -Infinity
    let maxLat = -Infinity
    const extend = (p: LatLng) => {
      minLng = Math.min(minLng, p.lng)
      minLat = Math.min(minLat, p.lat)
      maxLng = Math.max(maxLng, p.lng)
      maxLat = Math.max(maxLat, p.lat)
    }
    for (const overlay of overlays) {
      if (!overlay.visible) continue
      for (const f of overlay.features) {
        if (f.kind === "point") extend(f.position)
        else if (f.kind === "line") f.path.forEach(extend)
        else f.rings.forEach((ring) => ring.forEach(extend))
      }
    }
    if (Number.isFinite(minLng)) {
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 60, duration: 600 },
      )
    }
  }, [overlays])

  const onClick = useCallback(
    (e: MapMouseEvent) => {
      const features = e.features ?? []
      // Prefer a collar hit over the pasture beneath it.
      const collar = features.find(
        (f) => f.layer?.id === COLLAR_ICON_LAYER || f.layer?.id === COLLAR_CIRCLE_LAYER,
      )
      if (collar) {
        setSelectedCollar(String(collar.properties?.serialNo))
        setShowCollarInfoModal(true)
        return
      }
      const pasture = features.find((f) => f.layer?.id === PASTURE_FILL_LAYER)
      if (pasture) {
        const owner = ownersById[pasture.properties?.id]
        if (owner) {
          setSelectedOwner(owner)
          setShowUserInfoModal(true)
        }
      }
    },
    [ownersById],
  )

  const onMouseMove = useCallback((e: MapMouseEvent) => {
    const features = e.features ?? []
    const collar = features.find(
      (f) => f.layer?.id === COLLAR_ICON_LAYER || f.layer?.id === COLLAR_CIRCLE_LAYER,
    )
    const overlay = features.find((f) => isOverlayLayer(f.layer?.id))
    const canvas = e.target.getCanvas()
    const pos = { x: e.originalEvent.clientX, y: e.originalEvent.clientY }
    if (collar) {
      // A collar hit wins over an overlay beneath it.
      setHoveredSerial(Number(collar.properties?.serialNo))
      setHoveredOverlay(undefined)
      setTooltipPos(pos)
      canvas.style.cursor = "pointer"
    } else if (overlay && (overlay.properties?.name || overlay.properties?.description)) {
      setHoveredSerial(undefined)
      setHoveredOverlay({
        name: String(overlay.properties?.name ?? ""),
        description: String(overlay.properties?.description ?? ""),
      })
      setTooltipPos(pos)
      canvas.style.cursor = "pointer"
    } else {
      setHoveredSerial(undefined)
      setHoveredOverlay(undefined)
      setTooltipPos(undefined)
      canvas.style.cursor = features.length ? "pointer" : ""
    }
  }, [])

  const onMouseLeave = useCallback(() => {
    setHoveredSerial(undefined)
    setHoveredOverlay(undefined)
    setTooltipPos(undefined)
  }, [])

  // Tilt the camera when 3D terrain is selected so the relief reads; flatten otherwise.
  useEffect(() => {
    mapRef.current?.easeTo({ pitch: basemap === "terrain" ? TERRAIN_PITCH : 0, duration: 600 })
  }, [basemap])

  // Load the SVG icons into the map as named images for the symbol layer.
  // Drawn to a fixed-size canvas so SVGs without explicit pixel dims rasterize.
  const onLoad = useCallback((e: { target: any }) => {
    const map = e.target
    ;(window as any).map = map // 👈 expose to console
    console.log("Map exposed as window.map")
    const addImage = (name: string, data: ImageData) => {
      if (!map.hasImage(name)) map.addImage(name, data, { pixelRatio: 2 })
    }
    const rasterize = (name: string, url: string) => {
      const img = new Image(48, 48)
      img.onload = () => {
        const canvas = document.createElement("canvas")
        canvas.width = 48
        canvas.height = 48
        const ctx = canvas.getContext("2d")
        if (!ctx) return
        ctx.drawImage(img, 0, 0, 48, 48)
        const data = ctx.getImageData(0, 0, 48, 48)
        collarImages.current[name] = data
        addImage(name, data)
      }
      img.src = url
    }
    rasterize("collar-cattle", iconCattle)
    rasterize("collar-small", iconSmall)
    // A basemap switch reloads the style and drops added images; re-add each
    // one the moment a layer asks for it again.
    map.on("styleimagemissing", (ev: { id: string }) => {
      const data = collarImages.current[ev.id]
      if (data) addImage(ev.id, data)
    })
  }, [])

  const iconVisible = collarStyle === "icon"
  const terrainOn = basemap === "terrain"

  return (
    <div style={{ position: "relative", height, width: "100vw" }}>
      <Mapbox
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: INITIAL_CENTER.lng,
          latitude: INITIAL_CENTER.lat,
          zoom: INITIAL_ZOOM,
        }}
        minZoom={MIN_ZOOM}
        projection={{ name: projection }}
        mapStyle={BASEMAP_STYLE[basemap]}
        terrain={terrainOn ? { source: DEM_SOURCE, exaggeration: TERRAIN_EXAGGERATION } : undefined}
        style={{ height, width: "100vw" }}
        interactiveLayerIds={interactiveLayerIds}
        onLoad={onLoad}
        onClick={onClick}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
      >
        {terrainOn && (
          <Source
            id={DEM_SOURCE}
            type="raster-dem"
            url={DEM_URL}
            tileSize={512}
            maxzoom={14}
          />
        )}

        <Source id="pastures" type="geojson" data={pasturesFC}>
          <Layer
            id={PASTURE_FILL_LAYER}
            type="fill"
            paint={{ "fill-color": PASTURE_COLOR, "fill-opacity": 0.4 }}
          />
          <Layer
            id="pasture-outline"
            type="line"
            paint={{ "line-color": "#fff", "line-width": 2 }}
          />
        </Source>

        <Source id="collars" type="geojson" data={collarsFC}>
          <Layer {...collarIconLayer(iconVisible, collarOpacity)} />
          <Layer {...collarCircleLayer(!iconVisible, collarOpacity)} />
        </Source>

        {/* Overlays render above collars, each in its own source so opacity is
            an independent, per-overlay constant. */}
        {overlayLayers.map((o) => (
          <Source key={o.id} id={`overlay-${o.id}`} type="geojson" data={o.fc}>
            <Layer
              id={`overlay-fill-${o.id}`}
              type="fill"
              filter={["==", ["geometry-type"], "Polygon"]}
              paint={{ "fill-color": o.color, "fill-opacity": 0.5 * o.opacity }}
            />
            <Layer
              id={`overlay-line-${o.id}`}
              type="line"
              filter={["match", ["geometry-type"], ["LineString", "Polygon"], true, false]}
              paint={{ "line-color": o.color, "line-width": 3, "line-opacity": o.opacity }}
            />
            <Layer
              id={`overlay-point-${o.id}`}
              type="circle"
              filter={["==", ["geometry-type"], "Point"]}
              paint={{
                "circle-radius": 5,
                "circle-color": o.color,
                "circle-stroke-width": 1,
                "circle-stroke-color": "#fff",
                "circle-opacity": o.opacity,
                "circle-stroke-opacity": o.opacity,
              }}
            />
          </Source>
        ))}

        {userLocation && (
          <Marker longitude={userLocation.lng} latitude={userLocation.lat}>
            <img src={userLocationIcon} alt="you" width={28} height={28} />
          </Marker>
        )}
      </Mapbox>

      <MapInfo
        onRefresh={refresh}
        len_collars={visibleCollars.length}
        loading={loading}
        isStaff={isStaff}
      />

      {hoveredSerial !== undefined && tooltipPos && (
        <div
          style={{
            position: "fixed",
            left: tooltipPos.x,
            top: tooltipPos.y - 18,
            transform: "translate(-50%, -100%)",
            background: "rgba(255,255,255,0.97)",
            color: "#1a1a1a",
            padding: "6px 10px",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 500,
            whiteSpace: "nowrap",
            lineHeight: 1.2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            pointerEvents: "none",
            zIndex: 1000,
          }}
        >
          Serial {hoveredSerial}
        </div>
      )}

      {hoveredOverlay && tooltipPos && (
        <div
          style={{
            position: "fixed",
            left: tooltipPos.x,
            top: tooltipPos.y - 18,
            transform: "translate(-50%, -100%)",
            background: "rgba(255,255,255,0.97)",
            color: "#1a1a1a",
            padding: "6px 10px",
            borderRadius: 6,
            fontSize: 13,
            maxWidth: 260,
            lineHeight: 1.3,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            pointerEvents: "none",
            zIndex: 1000,
          }}
        >
          {hoveredOverlay.name && <div style={{ fontWeight: 600 }}>{hoveredOverlay.name}</div>}
          {hoveredOverlay.description && (
            <div style={{ fontWeight: 400, opacity: 0.8 }}>{hoveredOverlay.description}</div>
          )}
        </div>
      )}

      <CustomerInfoModal
        customer={selectedOwner}
        isOpen={showUserInfoModal}
        onClose={() => {
          setShowUserInfoModal(false)
          setSelectedOwner(undefined)
        }}
      />
      <CollarInfoModal
        serial={selectedCollar}
        isOpen={showCollarInfoModal}
        onClose={() => {
          setShowCollarInfoModal(false)
          setSelectedCollar(undefined)
        }}
      />
      <ForceUserLocationModal
        isOpen={showForceUserLocationModal && !isStaff}
        onGetLocation={getUserLocation}
      />
    </div>
  )
}

export default Map
